import { defineStore } from "pinia";
import { nodeTypes } from "@/constants/automation.js";
import { MarkerType } from "@vue-flow/core";
import {
  saveAutomationFlow,
  getAutomationData,
  getAutomationReportData,
  publishAutomation,
  renameAutomation,
  saveAutomationSettings,
  getCampaignDetails,
  disableAutomation,
} from "@/api/automation.js";
import { saveCampaign, getPreviewSize } from "@/api/campaign";
import dagre from "dagrejs";
const w = {
  addOnNode: 140,
  initial: 42,
  initial2: 42,
  stpoint: 196,
  stpoint2: 240,
  goTo: 240,
};
const width = 340;
const height = 100;
const nodesTypes = [...Object.values(nodeTypes), ...Object.keys(w)];
const insertNodes = (_this, elements, id, nodeType) => {
  let branch = "b";
  let tempNodes = {};
  const edgeType = _this.isPreview ? "emptyEdge" : "addOnEdge";
  const sorceElemnts = _this.getElementsByPrevId;
  if (sorceElemnts[id] && sorceElemnts[id].length) {
    sorceElemnts[id].forEach((node) => {
      let handle = [5, 6].includes(nodeType)
        ? node.branch_type
          ? "a"
          : "b"
        : "a";
      tempNodes[handle] = {
        ...node,
        data: { ...node.data, trigger_type: node.trigger_type },
        id: node.id.toString(),
        type: nodeTypes[node.node_type],
        position: { x: 0, y: 0 },
      };
      tempNodes[handle].class = (node.status ? "" : "disabled ") + "node-thumb";
      if (_this.getActionIds.includes(node.id)) {
        tempNodes[handle].class += " node-active";
      }
      elements.push({
        id: "e" + id + "-" + handle + "-" + node.id,
        source: id.toString(),
        sourceHandle: handle,
        target: node.id.toString(),
        type: edgeType,
        data: { branch_type: node.branch_type },
      });
      branch = handle === "b" ? "a" : "b";
      if (node.node_type === 7 && node.data.next_node_id) {
        elements.push({
          id: "e" + node.id + "-" + node.data.next_node_id,
          source: node.id.toString(),
          target: node.data.next_node_id.toString(),
          type: "emptyEdge",
          markerEnd: MarkerType.ArrowClosed,
          exclude: true,
          animated: true,
        });
      }
    });
  } else if (_this.action === "goto" && id === parseInt(_this.actionId)) {
    branch = _this.branchType ? "a" : "b";
    tempNodes[branch] = addGoToNode(id, elements, branch);
    branch = branch === "a" ? "b" : "a";
  } else if (nodeType !== 7) {
    tempNodes["a"] = addEndNode(id, elements, "a", nodeType, edgeType);
  }
  if (
    [5, 6].includes(nodeType) &&
    (!sorceElemnts[id] || sorceElemnts[id].length < 2)
  ) {
    const gotoBranch = _this.branchType ? "a" : "b";
    if (
      _this.action === "goto" &&
      id === parseInt(_this.actionId) &&
      gotoBranch === branch
    ) {
      tempNodes[branch] = addGoToNode(id, elements, branch);
    } else {
      tempNodes[branch] = addEndNode(id, elements, branch, nodeType, edgeType);
    }
  }
  if (tempNodes.a) {
    elements.push(tempNodes.a);
    if (!isNaN(tempNodes.a.id)) {
      insertNodes(
        _this,
        elements,
        parseInt(tempNodes.a.id),
        tempNodes.a.node_type
      );
    }
  }
  if (tempNodes.b) {
    elements.push(tempNodes.b);
    if (!isNaN(tempNodes.b.id)) {
      insertNodes(
        _this,
        elements,
        parseInt(tempNodes.b.id),
        tempNodes.b.node_type
      );
    }
  }
};
const addGoToNode = (id, elements, branch) => {
  elements.push({
    id: "e" + id + "-goto",
    source: id.toString(),
    target: "goto-" + id,
    sourceHandle: branch,
    type: "emptyEdge",
  });
  return {
    id: "goto-" + id,
    type: "goTo",
    position: { x: 0, y: 0 },
  };
};
const addEndNode = (id, elements, branch, nodeType, edgeType) => {
  branch = branch || "a";
  const branch_type = [5, 6].includes(nodeType) ? (branch === "a" ? 1 : 0) : 0;
  elements.push({
    id: "e-" + branch + "-" + id,
    source: id.toString(),
    sourceHandle: branch,
    target: "end-" + branch + "-" + id,
    type: edgeType,
    data: { branch_type },
  });
  return {
    id: "end-" + branch + "-" + id,
    type: "addOnNode",
    position: { x: 0, y: 0 },
    data: { prev_node_id: id },
  };
};
const getAllActionIds = (elementsByPrevId, actionId, ids = []) => {
  if (elementsByPrevId[actionId]) {
    elementsByPrevId[actionId].forEach((node) => {
      getAllActionIds(elementsByPrevId, node.id, ids);
    });
  }
  ids.push(actionId);
  return ids;
};

const getGotoNodes = (el, ids, elements, gotoElements) => {
  let id = el.id;
  if (!ids.includes(id)) {
    ids.push(id);
    el = elements[el.prev_node_id];
    if (el) {
      getGotoNodes(el, ids, elements, gotoElements);
    }
    el = gotoElements[id];
    if (el) {
      getGotoNodes(el, ids, elements, gotoElements);
    }
  }
};

const insertErrorNodes = (elementsByPrevId, elements, nodeId) => {
  if (elementsByPrevId[nodeId]) {
    let allowBlcklist = parseInt(window.localStorage.getItem("allowBlacklist"));
    elementsByPrevId[nodeId].forEach((node) => {
      if (
        [1, 2, 3, 6, 7, 8, 9].includes(node.error) ||
        (!allowBlcklist && node.error === 5)
      ) {
        elements.push(node);
      }
      insertErrorNodes(elementsByPrevId, elements, node.id);
    });
  }
};

export const useAutomationCreate = defineStore("automationCreate", {
  state: () => ({
    elements: {},
    actionId: 0,
    isPreview: false,
    automationName: "",
    action: "",
    branchType: 0,
    isPredefined: false,
    highlightError: false,
    autoDetails: {},
    campaign: {},
    sidebarHighlight: false,
    templateSize: "",
  }),
  getters: {
    getGotoExistNodes(state) {
      if (!["goto", "goto-edit", "new"].includes(state.action)) {
        return [];
      }
      let gotoElements = Object.values(state.elements)
        .filter((el) => el.node_type === 7)
        .reduce(
          (obj, item) => ({
            ...obj,
            [item.data.next_node_id]: item,
          }),
          {}
        );
      let el = state.elements[state.actionId];
      let ids = [];
      if (el) {
        getGotoNodes(el, ids, state.elements, gotoElements);
      }
      return ids;
    },
    getActionIds(state) {
      if (
        !state.actionId ||
        ["delete", "edit", "new", "goto"].includes(state.action)
      ) {
        return [];
      }
      if (["move", "clone", "goto-edit"].includes(state.action)) {
        return [parseInt(state.actionId)];
      }
      return getAllActionIds(
        this.getElementsByPrevId,
        parseInt(state.actionId)
      );
    },
    getElementsByPrevId(state) {
      let elements = {};
      for (var id in state.elements) {
        let node = state.elements[id];
        if (elements[node.prev_node_id]) {
          elements[node.prev_node_id].push({ ...node });
        } else {
          elements[node.prev_node_id] = [{ ...node }];
        }
      }
      return elements;
    },
    getElementWithError() {
      let elements = [];
      let hasInitialError = false;
      if (this.getElementsByPrevId[0]) {
        let initialNode = null;
        let allowBlacklist = parseInt(
          window.localStorage.getItem("allowBlacklist")
        );
        this.getElementsByPrevId[0].forEach((node) => {
          if (node.node_type === 1) {
            if (node.error) {
              if (node.error !== 11) {
                hasInitialError = true;
              }
              elements.push(node);
            }
          } else {
            initialNode = node;
          }
        });
        if (initialNode) {
          if (
            [1, 2, 3, 6, 7, 8, 9, 10, 11].includes(initialNode.error) ||
            (!allowBlacklist && initialNode.error === 5)
          ) {
            elements.push(initialNode);
          }
          insertErrorNodes(this.getElementsByPrevId, elements, initialNode.id);
        }
      }
      return { elements, count: elements.length, hasInitialError };
    },
    actionNodeType() {
      if (this.elements[this.actionId]) {
        return this.elements[this.actionId].node_type;
      }
      return 0;
    },
    getElements() {
      let elements = [];
      if (this.getElementsByPrevId[0]) {
        elements.push({
          id: "add-0",
          position: { x: 0, y: 0 },
          type: "initial",
        });
        let isInitalExists = false;
        let trggerNum = 0;
        this.getElementsByPrevId[0].forEach((node) => {
          let nodeData = { ...node.data };
          if (node.trigger_type) {
            nodeData.trigger_type = node.trigger_type;
          }
          let nodeClass = "node-start-thumb";
          if (node.node_type === 1) {
            trggerNum++;
            nodeData.trggerNum = trggerNum;
          } else {
            nodeClass = (node.status ? "" : "disabled ") + "node-thumb";
            if (this.getActionIds.includes(node.id)) {
              nodeClass += " node-active";
            }
          }
          elements.push({
            data: nodeData,
            id: node.id.toString(),
            type: nodeTypes[node.node_type],
            position: { x: 0, y: 0 },
            class: nodeClass,
          });
          if (node.node_type === 1) {
            elements.push({
              data: nodeData,
              id: "e" + node.id + "-0",
              source: node.id.toString(),
              target: "add-0",
              type: "otherCondition",
            });
          } else {
            isInitalExists = true;
            elements.push({
              id: "e0-" + node.id,
              source: "add-0",
              target: node.id.toString(),
              type: "emptyEdge",
            });
            insertNodes(this, elements, node.id, node.node_type);
          }
        });
        if (!isInitalExists) {
          elements[0].type = "initial2";
          elements.push(addEndNode("add-0", elements, "", 0, "edge"));
        }
        if (
          trggerNum < process.env.VUE_APP_AUTOMATION_TRIGGER_LIMIT &&
          !this.isPreview
        ) {
          elements.push({
            id: "new",
            position: { x: 0, y: 0 },
            type: "stpoint2",
            class: "node-start-thumb",
          });
        }
      } else {
        elements.push({
          id: "new",
          position: { x: 0, y: 0 },
          type: "stpoint",
        });
      }
      const dagreGraph = new dagre.graphlib.Graph();
      dagreGraph.setDefaultEdgeLabel(() => ({}));

      let nodes = elements.filter((e) => nodesTypes.includes(e.type));
      let edges = elements.filter((e) => !nodesTypes.includes(e.type));

      dagreGraph.setGraph({
        rankdir: "TB",
        marginy: 0,
        nodesep: 30,
        // ranker: "tight-tree",
      });

      let h = {
        sendMail: 120,
      };
      if (this.isPreview) {
        h.initial = h.initial2 = 40;
        w.initial = w.initial2 = 4;
      } else {
        h.initial = h.initial2 = 100;
        w.initial = 42;
        w.initial2 = 42;
      }
      nodes.forEach((node) => {
        dagreGraph.setNode(node.id, {
          width: w[node.type] || width,
          height: h[node.type] || height,
        });
      });

      edges.forEach((edge) => {
        if (!edge.exclude) {
          dagreGraph.setEdge(edge.source, edge.target);
        }
      });

      dagre.layout(dagreGraph, {
        keepNodeOrder: true,
        nodeOrder: nodes.map((n) => n.id),
      });

      let adjustX = Math.ceil(window.innerWidth / 2);
      let cn = nodes.filter((node) =>
        ["initial", "initial2", "stpoint"].includes(node.type)
      );
      if (cn.length) {
        const cnPos = dagreGraph.node(cn[0].id);
        adjustX -= cnPos.x;
      }
      nodes.forEach((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        // We are shifting the dagre node position (anchor=center center) to the top left
        // so it matches the React Flow node anchor point (top left).
        node.position = {
          x: nodeWithPosition.x + adjustX - (w[node.type] || width) / 2,
          y: nodeWithPosition.y - (h[node.type] || height) / 2,
        };

        if (["initial", "initial2"].includes(node.type)) {
          node.position.y += 80;
        }

        return node;
      });
      elements = [...nodes, ...edges];
      return elements;
    },
  },
  actions: {
    getCampaignDetails(id, isCreate) {
      if (this.campaign.id !== id) {
        this.campaign = { id };
        getCampaignDetails(id).then((campaign) => {
          if (isCreate) {
            id = null;
          }

          getPreviewSize({ camp_id: id, saved: 1 })
            .then((response) => {
              this.templateSize = response.size;
            })
            .catch(() => {
              this.templateSize = "";
            });

          this.campaign = { ...campaign, id };
        });
      }
    },
    saveCampaign(params) {
      return saveCampaign(params).then((campaign) => {
        this.campaign = { ...params, ...campaign };
        return campaign;
      });
    },
    updateAutomationStore(data) {
      Object.keys(data).forEach((key) => {
        this.autoDetails[key] = data[key];
      });
    },
    publishAutomation(id) {
      return publishAutomation(id);
    },
    renameAutomation(id, params) {
      return renameAutomation(id, params).then(() => {
        this.automationName = params.name;
      });
    },
    disableAutomation(id) {
      return disableAutomation(id);
    },
    saveAutomationSettings(id, params) {
      return saveAutomationSettings(id, params);
    },
    saveAutomationFlow(id, params, action) {
      return saveAutomationFlow(id, params, action).then((response) => {
        if (response.data) {
          response.data.forEach((node) => {
            this.elements[node.id] = node;
          });
        }
        if (response.deleted_nodes) {
          response.deleted_nodes.forEach((id) => {
            delete this.elements[id];
          });
        }
        return response;
      });
    },
    getAutomationData(id, preview = false) {
      const getData = preview ? getAutomationReportData : getAutomationData;
      this.isPreview = preview;
      return getData(id).then((response) => {
        if (response.data && response.data.nodes) {
          this.elements = {};
          this.isPredefined = !response.data.client_id;
          this.automationName = response.data.name;
          response.data.nodes.forEach((node) => {
            this.elements[node.id] = node;
          });
          delete response.data.nodes;
        }

        if (response.data) {
          this.autoDetails = response.data;
        }

        return response;
      });
    },
  },
});
